import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { CheckCheck, X, ExternalLink } from "lucide-react";
import { getErrorReports } from "../../features/error-reports/api/errorReportsApi";

/**
 * ResolvedIssuesNotifier — a non-blocking corner card that tells an agent when
 * an issue THEY reported (via Report an Issue) has been resolved, i.e. a
 * developer set it to "For Review" / "Agents to Review" on Monday. It appears
 * over any page (mounted in MainLayout), lists each resolved issue with its
 * reference number, area and who it's assigned to, and links out to the ticket.
 *
 * Design notes:
 *  - Reuses the SAME "ready to test" rule the Report an Issue page shows as a
 *    chip: sync_status === "synced" AND the board status is For/Agents to review.
 *  - Data comes from the existing GET /api/error-reports/reports/ feed, refreshed
 *    on a light poll. The board status itself is refreshed server-side from
 *    Monday every ~15 min, so a resolution surfaces within that window.
 *  - LAUNCH_CUTOFF scopes this to issues reported from go-live onward, so turning
 *    the feature on doesn't dump the whole backlog of already-resolved tickets on
 *    everyone ("only the reported today and going forward").
 *  - "Seen" and "don't show today" are remembered per-user in localStorage, so a
 *    resolution never nags twice and the checkbox silences it for the rest of the
 *    day. No backend change needed.
 */

// Only issues reported on/after go-live qualify (see LAUNCH_CUTOFF note above).
const LAUNCH_CUTOFF = "2026-09-11";
const READY_TO_TEST_BOARD_STATUSES = ["agents to review", "for review"];
const STAFF_ROLES = ["agent", "admin", "super_admin", "pa", "dev"];
const POLL_MS = 5 * 60 * 1000; // 5 min; board status itself updates every ~15 min

const todayStr = () => new Date().toISOString().slice(0, 10);
const seenKey = (email) => `resolvedIssues.seen.${(email || "").toLowerCase()}`;
const dismissKey = (email) => `resolvedIssues.dismissedToday.${(email || "").toLowerCase()}`;

// localStorage helpers - private-browsing safe (matches AgencyScopeSwitch).
const readSeen = (email) => {
  try {
    return new Set(JSON.parse(localStorage.getItem(seenKey(email)) || "[]"));
  } catch {
    return new Set();
  }
};
const writeSeen = (email, set) => {
  try {
    localStorage.setItem(seenKey(email), JSON.stringify([...set]));
  } catch { /* ignore */ }
};
const isDismissedToday = (email) => {
  try {
    return localStorage.getItem(dismissKey(email)) === todayStr();
  } catch {
    return false;
  }
};
const setDismissedToday = (email) => {
  try {
    localStorage.setItem(dismissKey(email), todayStr());
  } catch { /* ignore */ }
};

const isReadyToTest = (r) =>
  r.sync_status === "synced" &&
  READY_TO_TEST_BOARD_STATUSES.includes((r.monday_status_label || "").trim().toLowerCase());

const ResolvedIssuesNotifier = () => {
  const { user } = useSelector((state) => state.auth);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [dontShowToday, setDontShowToday] = useState(false);

  const email = user?.email || "";
  const eligible = !!email && STAFF_ROLES.includes(user?.role);

  const refresh = useCallback(async () => {
    if (!eligible || isDismissedToday(email)) return;
    let reports;
    try {
      const data = await getErrorReports({ limit: 100 });
      reports = Array.isArray(data) ? data : (data?.results || []);
    } catch {
      return; // a failed poll is silent; try again next interval
    }
    const seen = readSeen(email);
    const mine = reports.filter(
      (r) =>
        (r.reporter_email || "").toLowerCase() === email.toLowerCase() &&
        isReadyToTest(r) &&
        (r.created_at || "").slice(0, 10) >= LAUNCH_CUTOFF &&
        !seen.has(r.reference_number)
    );
    if (mine.length) {
      setItems(mine);
      setOpen(true);
    }
  }, [eligible, email]);

  useEffect(() => {
    if (!eligible) return undefined;
    refresh();
    const t = setInterval(refresh, POLL_MS);
    return () => clearInterval(t);
  }, [eligible, refresh]);

  // Acknowledge the listed issues so they never nag again; optionally silence any
  // further resolved-issue cards for the rest of today.
  const dismiss = () => {
    const seen = readSeen(email);
    items.forEach((r) => r.reference_number && seen.add(r.reference_number));
    writeSeen(email, seen);
    if (dontShowToday) setDismissedToday(email);
    setOpen(false);
    setItems([]);
  };

  if (!open || !items.length) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[9998] w-[360px] max-w-[calc(100vw-2rem)]">
      <div className="rounded-2xl border border-blue-200 bg-white shadow-2xl overflow-hidden dark:bg-gray-900 dark:border-blue-900/60">
        <div className="flex items-start gap-2 px-4 py-3 bg-blue-50 border-b border-blue-100 dark:bg-blue-950/40 dark:border-blue-900/60">
          <CheckCheck className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-blue-800 dark:text-blue-200">
              {items.length === 1
                ? "Your reported issue is resolved"
                : `${items.length} of your reported issues are resolved`}
            </p>
            <p className="text-[11px] text-blue-700/80 dark:text-blue-300/80">Ready for you to review and test.</p>
          </div>
          <button
            type="button"
            onClick={dismiss}
            title="Dismiss"
            className="text-blue-400 hover:text-blue-600 dark:hover:text-blue-200 shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="max-h-72 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800">
          {items.map((r) => (
            <div key={r.id} className="px-4 py-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] font-bold tracking-wide text-blue-700 dark:text-blue-300">
                  {r.reference_number}
                </span>
                {r.monday_item_url && (
                  <a
                    href={r.monday_item_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-600 hover:underline dark:text-blue-400"
                  >
                    View <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
              <p className="text-sm font-medium text-gray-800 dark:text-gray-100 mt-0.5 break-words">{r.title}</p>
              <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                {r.area && <span>Area: <span className="font-medium text-gray-700 dark:text-gray-300">{r.area}</span></span>}
                {r.assignee_name && (
                  <span>Assigned to: <span className="font-medium text-gray-700 dark:text-gray-300">{r.assignee_name}</span></span>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-gray-100 dark:border-gray-800">
          <label className="flex items-center gap-2 text-[11px] text-gray-600 dark:text-gray-400 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={dontShowToday}
              onChange={(e) => setDontShowToday(e.target.checked)}
              className="accent-blue-600"
            />
            Don't show again today
          </label>
          <button
            type="button"
            onClick={dismiss}
            className="text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg px-4 py-2 transition-colors"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResolvedIssuesNotifier;
